import { useEffect, useRef, useState } from 'react'
import { type TooltipRefProps } from 'react-tooltip'

import { AppsflyerEvent, sendAppsflyer } from '@/shared/metrics'
import { Button, ModalOverlay } from '@/shared/ui'

import { CustomTooltip } from '../custom-tooltip'
import { openTooltipWhenReady } from '../lib/open-tooltip-when-ready'
import { parseTimeToMs } from '../lib/parse-time-to-ms'

import styles from './driver-recharge-tooltip.module.scss'

interface IDriverRechargeTooltipProps {
    amountRef: React.RefObject<HTMLDivElement | null>
    contentRef: React.RefObject<HTMLFormElement | null>
    onClose: () => void
    radioRef: React.RefObject<HTMLDivElement | null>
    setShowTooltip: (show: boolean) => void
}

export const DriverRechargeTooltip = ({
    contentRef,
    amountRef,
    radioRef,
    setShowTooltip,
    onClose,
}: IDriverRechargeTooltipProps): React.ReactNode => {
    const tooltip1Ref = useRef<TooltipRefProps>(null)
    const tooltip2Ref = useRef<TooltipRefProps>(null)
    const tooltipContent1Ref = useRef<HTMLDivElement>(null)
    const tooltipContent2Ref = useRef<HTMLDivElement>(null)
    const [isModalOpen, setIsModalOpen] = useState(true)

    const transitionShowDelayRef = useRef(0)

    // Открытие тултипа прогрузке страницы
    useEffect(() => {
        transitionShowDelayRef.current = parseTimeToMs(
            getComputedStyle(document.documentElement).getPropertyValue(
                '--rt-transition-closing-delay',
            ),
        )
        sendAppsflyer(AppsflyerEvent.topup_onboarding_sum_view, {})
        openTooltipWhenReady('#amount-tooltip', tooltip1Ref, () => {
            if (amountRef.current && contentRef.current) {
                contentRef.current.classList.add(styles['custom-tooltip__no-click'])
                amountRef.current.classList.add(styles['custom-tooltip__focus'])
            }
        })
    }, [amountRef, contentRef])

    const handleFirstTooltipClose = () => {
        tooltip1Ref.current?.close()
        sendAppsflyer(AppsflyerEvent.topup_onboarding_sum_click, {})
        setTimeout(() => {
            if (amountRef.current) {
                amountRef.current.classList.remove(styles['custom-tooltip__focus'])
            }
        }, transitionShowDelayRef.current)
        setTimeout(() => {
            sendAppsflyer(AppsflyerEvent.topup_onboarding_method_view, {})
            openTooltipWhenReady('#selector-tooltip', tooltip2Ref, () => {
                if (radioRef.current) {
                    radioRef.current.classList.add(styles['custom-tooltip__focus'])
                }
            })
        }, transitionShowDelayRef.current)
    }

    const handleSecondTooltipClose = () => {
        tooltip2Ref.current?.close()
        sendAppsflyer(AppsflyerEvent.topup_onboarding_method_click, {})
        setTimeout(() => {
            setIsModalOpen(false)
            onClose()
            if (radioRef.current && contentRef.current) {
                contentRef.current.classList.remove(styles['custom-tooltip__no-click'])
                radioRef.current.classList.remove(styles['custom-tooltip__focus'])
            }
        }, transitionShowDelayRef.current)
        setShowTooltip(false)
    }

    // Обработчик для предотвращения закрытия тултипа при клике внутри него
    useEffect(() => {
        const handleDocumentClick = (event: MouseEvent): void => {
            const target = event.target as HTMLElement
            const isInsideTooltip1 =
                tooltipContent1Ref.current && tooltipContent1Ref.current.contains(target)
            const isInsideTooltip2 =
                tooltipContent2Ref.current && tooltipContent2Ref.current.contains(target)

            if (isInsideTooltip1 || isInsideTooltip2) {
                const button = target.closest('button')

                if (!button) {
                    event.stopPropagation()
                    event.preventDefault()
                }
            }
        }

        document.addEventListener('click', handleDocumentClick, true)

        return () => {
            document.removeEventListener('click', handleDocumentClick, true)
        }
    }, [])

    return (
        <>
            {isModalOpen && <ModalOverlay onClose={() => {}} />}
            <CustomTooltip isFirst={true} tooltipRef={tooltip1Ref}>
                <div ref={tooltipContent1Ref} className={styles['driver-recharge-tooltip']}>
                    <p className={styles['driver-recharge-tooltip__text']}>
                        Введите сумму или
                        <br />
                        выберите из вариантов
                    </p>
                    <Button
                        className={styles['driver-recharge-tooltip__button']}
                        size='small'
                        variant='secondary'
                        onClick={handleFirstTooltipClose}
                    >
                        Дальше
                    </Button>
                </div>
            </CustomTooltip>

            <CustomTooltip tooltipRef={tooltip2Ref}>
                <div ref={tooltipContent2Ref} className={styles['driver-recharge-tooltip']}>
                    <p className={styles['driver-recharge-tooltip__text']}>
                        Выберите способ
                        <br /> пополнения, и мы <br />
                        запомним его
                    </p>
                    <Button
                        className={styles['driver-recharge-tooltip__button']}
                        size='small'
                        variant='secondary'
                        onClick={handleSecondTooltipClose}
                    >
                        Понятно
                    </Button>
                </div>
            </CustomTooltip>
        </>
    )
}
